import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BiExit } from "react-icons/bi";
import {
  HiOutlineClipboardDocumentCheck,
  HiOutlineCurrencyDollar,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import CustomButton from "components/common/Button/CustomButton";
import SelectOption from "components/common/Select/Select";
import SideBar from "components/common/SideBar/SideBarNew";

import { LoginContext } from "helpers/LoginContext";
import { roleAccess, usePermissionsVersion } from "helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { connect, useDispatch } from "react-redux";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import {
  ROSTERING_INVOICING_BREADCRUMB,
  ROSTERING_INVOICING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import {
  getHRMTeamManagmentRequest,
  getSPParticipantsListRequest,
  spRosteringSettingsRequest,
} from "store/actions/sp.actions";
import {
  getParticipantsList,
  getTeamManagmentList,
  getSpSetting,
  getSpRosteringSetting,
} from "store/selector/sp.selector";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import TimesheetTable from "./TimesheetTable";
import TimeSheetBreakDown from "../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation";
import InvoicingWorkflowLayout, {
  InvoicingWorkflowFilterField,
} from "../components/InvoicingWorkflowLayout";
import "./billable-shifts.css";

import { capitalizeWords, addMonths, getRosteringDefaultDateRange } from "Utils/utils";
import {
  isRequestCancelled,
  replaceAbortController,
} from "Utils/requestCancel";
import Pagination from "components/common/Pagination";
import { emptyPaginationObj } from "Constants/constant";
import { getParticipantLabel } from "Constants/constant";
import { useNavigate } from "react-router";
import { apiGetRequest } from "../../../../helpers/Requests";
import {
  URL_V5_SHIFT_BILLING_ALLOCATION,
  URL_V5_SHIFTS_BILLING_ALLOCATION_LIST,
} from "../../../../store/services/URL";
import moment from "moment";
import {
  buildShiftBillingListQuery,
  formatBillingParticipantNames,
  formatShiftBillingCurrency,
  formatShiftBillingHours,
  formatShiftListWorkerNames,
  getShiftBillingModeLabel,
  parseShiftBillingDetailResponse,
  parseShiftBillingListResponse,
  resolveShiftListAmount,
  resolveShiftListDate,
  resolveShiftListDuration,
  summarizeShiftBillingListPage,
  TIMESHEET_STATUS_FILTER_OPTIONS,
} from "../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";

const TimesheetList = ({
  teamManagementList,
  spParticipants,
  getTeamManagmentListCall,
  getParticipantList,
  settings,
  spRosteringSetting,
}) => {
  const { sideBarPadding } = React.useContext(LoginContext);
  const participantLabel = getParticipantLabel(settings?.care_sector, false);
  const participantLabelPlural = getParticipantLabel(settings?.care_sector, true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedTimesheetStatus, setSelectedTimesheetStatus] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const permissionsVersion = usePermissionsVersion();
  const canAccessCreateInvoice = useMemo(
    () =>
      roleAccess(
        [],
        PERMISSION_KEYS.ROSTER_INVOICE,
        PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.CREATE
      ),
    [permissionsVersion]
  );

  useEffect(() => {
    dispatch(spRosteringSettingsRequest());
  }, [dispatch]);

  useEffect(() => {
    const defaultRange = getRosteringDefaultDateRange(spRosteringSetting);
    if (!defaultRange) return;

    setFromDate(defaultRange.fromDate);
    setToDate(defaultRange.toDate);
    setSelectedFromDate(new Date(defaultRange.fromDate));
  }, [spRosteringSetting]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const activeDetailRequestRef = useRef(null);
  const detailAbortRef = useRef(null);
  const listAbortRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const detailPanelRef = useRef(null);
  const [shiftList, setShiftList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [perPage, setPerPage] = useState({
    label: "10",
    value: 10,
  });

  const PER_PAGE_VALUES = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
    { label: "150", value: 150 },
  ];

  const fetchShiftList = useCallback(
    (page = 1) => {
      const requestId = ++listRequestIdRef.current;
      listAbortRef.current = replaceAbortController(listAbortRef.current);
      const signal = listAbortRef.current?.signal;

      setListLoading(true);
      const query = buildShiftBillingListQuery({
        page,
        perPage: perPage?.value ?? 10,
        selectedWorker,
        selectedParticipant,
        selectedTimesheetStatus,
        fromDate,
        toDate,
      });

      return apiGetRequest(
        `${URL_V5_SHIFTS_BILLING_ALLOCATION_LIST}?${query}`,
        null,
        signal ? { signal } : {}
      )
        .then((res) => {
          if (requestId !== listRequestIdRef.current) return;
          const { rows, page: currentPage, per_page, total } =
            parseShiftBillingListResponse(res, {
              fallbackPage: page,
              fallbackPerPage: perPage?.value ?? 10,
            });
          setShiftList(rows);
          setPaginationObj({
            page: currentPage,
            per_page,
            total,
            totalPages: Math.ceil(total / (per_page || 1)),
          });
        })
        .catch((err) => {
          if (isRequestCancelled(err)) return;
          if (requestId !== listRequestIdRef.current) return;
          console.error("Error fetching billable shifts:", err);
          setShiftList([]);
          setPaginationObj(emptyPaginationObj);
        })
        .finally(() => {
          if (requestId !== listRequestIdRef.current) return;
          setListLoading(false);
        });
    },
    [perPage?.value, selectedWorker, selectedParticipant, selectedTimesheetStatus, fromDate, toDate]
  );

  const fetchShiftBillingDetail = useCallback((shiftUuid) => {
    activeDetailRequestRef.current = shiftUuid;
    detailAbortRef.current = replaceAbortController(detailAbortRef.current);
    const signal = detailAbortRef.current?.signal;
    setLoadingRowId(shiftUuid);
    return apiGetRequest(
      `${URL_V5_SHIFT_BILLING_ALLOCATION(shiftUuid)}?ensure_lines=true&auto_allocate=true`,
      null,
      signal ? { signal } : {}
    )
      .then((res) => {
        if (activeDetailRequestRef.current !== shiftUuid) return;
        setSelectedItem(parseShiftBillingDetailResponse(res));
      })
      .catch((err) => {
        if (isRequestCancelled(err)) return;
        if (activeDetailRequestRef.current !== shiftUuid) return;
        console.error("Error fetching shift billing details:", err);
        setSelectedItem(null);
      })
      .finally(() => {
        if (activeDetailRequestRef.current === shiftUuid) {
          setLoadingRowId(null);
        }
      });
  }, []);

  const handleRowSelect = useCallback(
    (shiftUuid) => {
      if (!shiftUuid) return;

      const isAlreadySelected = selectedItem?.shift_uuid === shiftUuid;
      const isCurrentlyLoading = loadingRowId === shiftUuid;

      if (isAlreadySelected || isCurrentlyLoading) {
        activeDetailRequestRef.current = null;
        try {
          detailAbortRef.current?.abort?.();
        } catch {
          // ignore
        }
        setSelectedItem(null);
        setLoadingRowId(null);
        return;
      }

      fetchShiftBillingDetail(shiftUuid);
    },
    [selectedItem?.shift_uuid, loadingRowId, fetchShiftBillingDetail]
  );

  const handleRefreshAfterSave = useCallback(() => {
    const currentPage = paginationObj.page || 1;
    fetchShiftList(currentPage).then(() => {
      const shiftUuid = selectedItem?.shift_uuid;
      if (shiftUuid) {
        fetchShiftBillingDetail(shiftUuid);
      }
    });
  }, [
    fetchShiftList,
    fetchShiftBillingDetail,
    paginationObj.page,
    selectedItem?.shift_uuid,
  ]);

  const dropdownList = useMemo(() => {
    const newParticipants =
      spParticipants?.participant?.data
        ?.slice()
        .sort((a, b) => {
          const nameA = `${(a?.first_name || "").toLowerCase()} ${(a?.last_name || "").toLowerCase()}`;
          const nameB = `${(b?.first_name || "").toLowerCase()} ${(b?.last_name || "").toLowerCase()}`;
          return nameA.localeCompare(nameB);
        })
        .map((item) => ({
          label: `${capitalizeWords(item?.first_name)} ${capitalizeWords(item?.last_name || "")}`,
          value: item?.id,
        })) || [];

    const newWorkers =
      teamManagementList?.employees?.data
        ?.slice()
        .sort((a, b) => {
          const nameA = `${(a?.first_name || "").toLowerCase()} ${(a?.last_name || "").toLowerCase()}`;
          const nameB = `${(b?.first_name || "").toLowerCase()} ${(b?.last_name || "").toLowerCase()}`;
          return nameA.localeCompare(nameB);
        })
        .map((item) => ({
          label: `${item?.first_name} ${item?.last_name}`,
          value: item?.id,
        })) || [];

    return {
      participants: [{ label: "All", value: "all" }, ...newParticipants],
      workers: [{ label: "All", value: "all" }, ...newWorkers],
    };
  }, [spParticipants, teamManagementList]);

  useEffect(() => {
    const filters = {
      page: 1,
      per_page: 500,
      search: null,
    };

    getTeamManagmentListCall({ filters, bodyFilter: null });
    getParticipantList({ filters, bodyFilter: null });
  }, [getTeamManagmentListCall, getParticipantList]);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    setSelectedItem(null);
    fetchShiftList(1);
  }, [fromDate, toDate, selectedParticipant, selectedWorker, selectedTimesheetStatus, perPage?.value, fetchShiftList]);

  useEffect(
    () => () => {
      listRequestIdRef.current += 1;
      activeDetailRequestRef.current = null;
      try {
        listAbortRef.current?.abort?.();
        detailAbortRef.current?.abort?.();
      } catch {
        // ignore
      }
    },
    []
  );

  const selectedShiftUuid = selectedItem?.shift_uuid;

  useEffect(() => {
    if (!selectedItem || loadingRowId) return undefined;

    const timer = setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    return () => clearTimeout(timer);
  }, [selectedItem?.shift_uuid, loadingRowId]);

  const pageMetrics = useMemo(
    () => summarizeShiftBillingListPage(shiftList),
    [shiftList]
  );

  const selectedShiftSummary = useMemo(() => {
    if (!selectedItem) return null;

    const listRow = shiftList.find(
      (row) => row.shift_uuid === selectedItem.shift_uuid
    );
    const amount =
      resolveShiftListAmount(selectedItem) ?? resolveShiftListAmount(listRow);
    const shiftDate =
      resolveShiftListDate(selectedItem) ?? resolveShiftListDate(listRow);
    const duration =
      resolveShiftListDuration(selectedItem) ??
      resolveShiftListDuration(listRow);
    const participantNames = formatBillingParticipantNames(
      selectedItem?.participants ?? listRow?.participants ?? []
    );
    const workerNames = formatShiftListWorkerNames(selectedItem ?? listRow);
    const billingMode = getShiftBillingModeLabel(selectedItem ?? listRow);

    return {
      amount,
      shiftDate,
      duration,
      participantNames,
      workerNames,
      billingMode,
    };
  }, [selectedItem, shiftList]);

  const filterSummary = useMemo(() => {
    const parts = [];

    if (fromDate && toDate) {
      parts.push(
        `${moment(fromDate).format("D MMM YY")} – ${moment(toDate).format("D MMM YY")}`
      );
    }

    if (selectedParticipant?.value && selectedParticipant.value !== "all") {
      parts.push(selectedParticipant.label);
    }

    if (selectedWorker?.value && selectedWorker.value !== "all") {
      parts.push(selectedWorker.label);
    }

    if (selectedTimesheetStatus?.value && selectedTimesheetStatus.value !== "all") {
      parts.push(selectedTimesheetStatus.label);
    }

    return parts.join(" · ") || "All shifts in date range";
  }, [fromDate, toDate, selectedParticipant, selectedWorker, selectedTimesheetStatus]);

  const billableShiftsSubValue = useMemo(() => {
    const total = paginationObj.total ?? 0;
    if (!total) return "Adjust filters to find billable shifts";

    return filterSummary;
  }, [paginationObj.total, filterSummary]);

  const pageValueSubValue = useMemo(() => {
    if (!pageMetrics.pageCount) return "No value on this page";

    return `${formatShiftBillingHours(pageMetrics.pageTotalHours)} on page`;
  }, [pageMetrics]);

  const selectedShiftCard = useMemo(() => {
    const activeUuid = selectedItem?.shift_uuid ?? loadingRowId;

    if (!activeUuid) {
      return {
        value: "—",
        label: "Selected shift value",
        highlight: false,
      };
    }

    const listRow = shiftList.find((row) => row.shift_uuid === activeUuid);
    const amount =
      resolveShiftListAmount(selectedItem) ?? resolveShiftListAmount(listRow);

    if (loadingRowId === activeUuid && !selectedItem && !listRow) {
      return {
        value: "…",
        label: "Selected shift value",
        highlight: true,
      };
    }

    return {
      value: formatShiftBillingCurrency(amount) ?? "N/A",
      label: "Selected shift value",
      highlight: true,
    };
  }, [selectedItem, loadingRowId, shiftList]);

  const workflowStats = useMemo(
    () => [
      {
        icon: HiOutlineDocumentText,
        value: paginationObj.total ?? 0,
        label: "Billable shifts",
        subValue: billableShiftsSubValue,
        iconVariant: "blue",
      },
      {
        icon: HiOutlineCurrencyDollar,
        value:
          formatShiftBillingCurrency(pageMetrics.pageTotalAmount) ?? "$0.00",
        label: "Page billable value",
        subValue: pageValueSubValue,
        iconVariant: "teal",
      },
      {
        icon: HiOutlineClipboardDocumentCheck,
        value: selectedShiftCard.value,
        label: selectedShiftCard.label,
        iconVariant: "amber",
        highlight: selectedShiftCard.highlight,
      },
    ],
    [
      paginationObj.total,
      billableShiftsSubValue,
      pageMetrics.pageTotalAmount,
      pageValueSubValue,
      selectedShiftCard,
    ]
  );

  return (
    <>
      <div className='min-h-full'>
        <SideBar />
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300"
          }  flex flex-col`}
        >
          <main className="h-screen flex-1 bg-[#f1f5f9]">
            <Header
              breadcrumb={
                <Breadcrumb
                  pages={ROSTERING_INVOICING_BREADCRUMB(
                    ROSTERING_INVOICING_BREADCRUMB_PAGES.ROSTER_INVOICE_CODEALLOCATION
                  )}
                />
              }
            />
            <InvoicingWorkflowLayout
              activeStepId="allocation"
              stats={workflowStats}
              headerActions={
                canAccessCreateInvoice ? (
                  <CustomButton
                    clickHandler={() => navigate("/rostering/create-roster-invoice")}
                    Icon={<BiExit />}
                    label={
                      <span className="whitespace-nowrap">Go to create invoice</span>
                    }
                  />
                ) : null
              }
              filters={
                <>
                  <InvoicingWorkflowFilterField label={participantLabel}>
                    <SelectOption
                      isClearable={true}
                      newDesign={true}
                      isDisabled={listLoading}
                      cssClass=""
                      name="participant"
                      isSearch={true}
                      data={dropdownList?.participants}
                      selectedOption={selectedParticipant}
                      handleChange={(e) => {
                        setSelectedParticipant(e);
                      }}
                      placeHolder={`All ${participantLabel}s`}
                      maxHeight="350px"
                    />
                  </InvoicingWorkflowFilterField>
                  <InvoicingWorkflowFilterField label="Worker">
                    <SelectOption
                      isClearable={true}
                      newDesign={true}
                      isDisabled={listLoading}
                      cssClass=""
                      name="worker"
                      isSearch={true}
                      data={dropdownList?.workers}
                      selectedOption={selectedWorker}
                      handleChange={(e) => {
                        setSelectedWorker(e);
                      }}
                      placeHolder="All workers"
                      maxHeight="350px"
                    />
                  </InvoicingWorkflowFilterField>
                  <InvoicingWorkflowFilterField label="Delivered date">
                    <DateRangePickerNew
                      placeholder="Delivered Date Range"
                      isClearable={false}
                      timesheet
                      isDisabled={listLoading}
                      fromDate={fromDate}
                      toDate={toDate}
                      setSelectedFromDate={setSelectedFromDate}
                      maxDate={addMonths(selectedFromDate, 3)}
                      handleChange={(obj) => {
                        if (obj?.fromDate && obj?.toDate) {
                          setFromDate(obj.fromDate);
                          setToDate(obj.toDate);
                          setSelectedItem(null);
                        }
                      }}
                    />
                  </InvoicingWorkflowFilterField>
                  <InvoicingWorkflowFilterField label="Timesheet Status">
                    <SelectOption
                      isClearable={true}
                      newDesign={true}
                      isDisabled={listLoading}
                      cssClass=""
                      name="timesheet_status"
                      isSearch={false}
                      data={TIMESHEET_STATUS_FILTER_OPTIONS}
                      selectedOption={selectedTimesheetStatus}
                      handleChange={(e) => {
                        setSelectedTimesheetStatus(e);
                      }}
                      placeHolder="All statuses"
                      maxHeight="350px"
                    />
                  </InvoicingWorkflowFilterField>
                </>
              }
            >
              <div className="billable-shifts-page">
                <div className="bs-table-card">
                  <div className="bs-table-card-header">
                    <div className="bs-table-card-heading">
                      <h2 className="bs-table-card-title">
                        Billable Shifts
                        <span className="bs-table-card-count">
                          ({paginationObj.total ?? 0})
                        </span>
                      </h2>
                      {pageMetrics.pageCount > 0 && (
                        <div className="bs-table-card-summary">
                          <span className="bs-summary-chip bs-summary-chip--value">
                            {formatShiftBillingHours(pageMetrics.pageTotalHours)} on page
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`bs-table-scroll custom-scroll ${
                      selectedItem ? "is-compact" : "is-expanded"
                    }`}
                  >
                    <table className="bs-table">
                      <thead>
                        <tr>
                          <th scope="col" aria-hidden />
                          <th scope="col" className="bs-th-worker normal-case">
                            {participantLabelPlural}
                          </th>
                          <th scope="col" className="bs-th-worker">
                            Worker
                          </th>
                          <th scope="col" className="bs-th-center">
                            Timesheet Status
                          </th>
                          <th scope="col" className="bs-th-center">
                            Delivered Date
                          </th>
                          <th scope="col" className="bs-th-center">
                            Type Of Service
                          </th>
                          <th scope="col" className="bs-th-center">
                            Quantity
                          </th>
                          <th scope="col" className="bs-th-center">
                            Total Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {listLoading ? (
                          <tr className="bs-loading-row">
                            <td colSpan={8}>
                              <CircularLoader
                                classes="flex justify-center items-center"
                                classloader="loader-l"
                              />
                            </td>
                          </tr>
                        ) : shiftList.length > 0 ? (
                          shiftList.map((item) => (
                            <TimesheetTable
                              item={item}
                              key={item.shift_uuid ?? item.id}
                              selected={selectedShiftUuid === item.shift_uuid}
                              onSelect={handleRowSelect}
                              isLoading={loadingRowId === item.shift_uuid}
                            />
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="bs-empty-state">
                              <strong>No shifts found</strong>
                              Adjust your filters or date range to see billable
                              shifts.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {paginationObj.total > 0 && (
                    <div className="bs-table-footer">
                      <Pagination
                        newDesign={true}
                        showPerPageDropdown={true}
                        handleLimitChange={setPerPage}
                        perPageData={PER_PAGE_VALUES}
                        perPageValue={perPage}
                        page={paginationObj.page}
                        activeListLength={shiftList}
                        limit={paginationObj.per_page}
                        totalList={paginationObj.total}
                        handlePagination={(e) => {
                          setSelectedItem(null);
                          fetchShiftList(e);
                        }}
                        totalPages={paginationObj.totalPages}
                      />
                    </div>
                  )}
                </div>

                {selectedItem && selectedShiftSummary && (
                  <div className="bs-detail-panel" ref={detailPanelRef}>
                    <div className="bs-detail-panel-header">
                      <div className="bs-detail-panel-summary">
                        <div className="bs-detail-panel-top">
                          <span className="bs-detail-panel-label">Code allocation</span>
                          {selectedShiftSummary.shiftDate && (
                            <span className="bs-detail-panel-date">
                              {moment(selectedShiftSummary.shiftDate).format(
                                "ddd, D MMM YYYY"
                              )}
                            </span>
                          )}
                        </div>
                        <h3 className="bs-detail-panel-title">
                          {selectedShiftSummary.participantNames || "Selected shift"}
                        </h3>
                        <div className="bs-detail-panel-meta">
                          {selectedShiftSummary.workerNames && (
                            <span className="bs-detail-panel-worker">
                              {selectedShiftSummary.workerNames}
                            </span>
                          )}
                          <span className="bs-badge bs-badge--mode">
                            {selectedShiftSummary.billingMode}
                          </span>
                          {formatShiftBillingCurrency(selectedShiftSummary.amount) && (
                            <span className="bs-detail-panel-amount">
                              {formatShiftBillingCurrency(selectedShiftSummary.amount)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <TimeSheetBreakDown
                      shiftDetails={selectedItem}
                      setShiftDetails={setSelectedItem}
                      hidePayrollFields
                      onUpdateSuccess={handleRefreshAfterSave}
                    />
                  </div>
                )}
              </div>
            </InvoicingWorkflowLayout>
          </main>
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  getTeamManagmentListCall: getHRMTeamManagmentRequest,
  getParticipantList: getSPParticipantsListRequest,
};

const mapStateToProps = (state) => ({
  teamManagementList: getTeamManagmentList(state),
  spParticipants: getParticipantsList(state),
  settings: getSpSetting(state),
  spRosteringSetting: getSpRosteringSetting(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimesheetList);
